<template>
  <v-dialog :value="isVisible" fullscreen persistent transition="dialog-bottom-transition">
    <v-card>
      <Toolbar title="Upload a Bulk Claims File" icon="mdi-file-delimited" @close="closeWizard" />
      <div :class="isMobile ? 'csv-wizard-container-mobile' : 'csv-wizard-container'">
        <v-row>
          <v-col :cols="colsSpan">
            <div :class="isMobile ? 'csv-internal-container-mobile' : 'csv-internal-container'">
              <div :class="isMobile ? 'nav-container-mobile' : 'nav-container'">
                <Button v-if="showResetButton" icon-left="mdi-chevron-left" @click="back"
                  >Back</Button
                >
                <PillNav :steps="pillNavSteps" color="brandGreen" width="100%" :step="stepId" />
                <Button
                  :icon-right="stepId !== 3 ? 'mdi-chevron-right' : ''"
                  :disabled="!nextEnabled"
                  @click="handleNext"
                  :loading="nextLoading"
                >
                  <template v-if="stepId === 3">
                    <v-icon>mdi-check-circle</v-icon>
                  </template>
                  <template v-else>Next</template>
                </Button>
              </div>
              <UploadCSV
                v-if="stepId === 1"
                data-testid="csv-wizard-step-1"
                @file-changed="onFileChanged"
                :file="file"
                :columns="columns"
              />
              <MapColumns
                v-if="stepId === 2"
                data-testid="csv-wizard-step-2"
                :rows="rows"
                :columns="columns"
                @change="onMappedRowsChanged"
                @delete-row="onDeleteRow"
              />
              <AllDone v-if="stepId === 3" data-testid="csv-wizard-step-3" @close="closeWizard" />
            </div>
          </v-col>
        </v-row>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import Toolbar from "@/components/generic/Toolbar";
import Button from "@/components/generic/Button";
import PillNav from "@/components/PillNav";
import UploadCSV from "@/components/csv-wizard/steps/UploadCSV";
import MapColumns from "@/components/csv-wizard/steps/MapColumns";
import AllDone from "@/components/csv-wizard/steps/AllDone";
import UserService from "@/services/UserService";
export default {
  name: "CSVWizard",
  components: {
    Toolbar,
    PillNav,
    Button,
    UploadCSV,
    MapColumns,
    AllDone,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["submitted"],
  data() {
    return {
      stepId: 1,
      pillNavSteps: [
        { step: 1, icon: "file" },
        { step: 2, icon: "format-list-bulleted" },
        { step: 3, icon: "rocket-launch" },
      ],
      file: null,
      nextLoading: false,
      rows: [],
      mappedRowsWithHeaders: [],
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    isVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    showResetButton() {
      return this.stepId === 2;
    },
    nextEnabled() {
      if (this.stepId === 1) {
        return !!this.file;
      } else if (this.stepId === 2) {
        return this.mappedRowsWithHeaders.length > 0;
      }
      return true;
    },
    colsSpan() {
      if (this.stepId === 2) {
        return 12;
      }
      return this.isMobile ? 12 : 6;
    },
  },
  methods: {
    closeWizard() {
      this.$emit("close");
      this.isVisible = false;
      this.file = null;
      this.back();
    },
    onFileChanged(file) {
      this.file = file;
    },
    async handleNext() {
      this.nextLoading = true;

      try {
        if (this.stepId === 1) {
          await this.convertCSVToJson();
        } else if (this.stepId === 2) {
          this.$emit("submitted", this.mappedRowsWithHeaders);
        } else if (this.stepId === 3) {
          this.closeWizard();
          return;
        }
        this.stepId++;
      } catch (error) {
        console.error("Error handling next step ", error);

        this.$store.dispatch("showError", {
          message: "Error processing CSV",
        });
        this.file = null;
      } finally {
        this.nextLoading = false;
      }
    },
    async convertCSVToJson() {
      const response = await UserService.csvToJson(this.file);
      this.rows = response.result.rows;
    },
    back() {
      this.stepId = 1;
    },
    onMappedRowsChanged(output) {
      this.mappedRowsWithHeaders = output;
    },
    onDeleteRow(index) {
      this.rows.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.v-card {
  width: 100%;
}

.csv-wizard-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 1440px;
  padding-left: 4rem;
  padding-right: 4rem;
  padding-top: 3rem;
  padding-bottom: 3rem;
  margin: 0 auto;
}

.csv-wizard-container-mobile {
  padding: 1rem;
}

.row {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center !important;
}

.nav-container {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.nav-container-mobile {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.nav-container-mobile > *:first-child {
  align-self: flex-start;
}

.csv-internal-container {
  height: 100%;
  display: flex;
  flex-direction: column;

  min-height: 720px;
  padding: 3rem;
  align-self: center;
}

.csv-internal-container-mobile {
  padding: 1rem;
}
</style>
